import { VideoCreationRequestInternalModelId } from "@apis/generated/openapi";
import { useOpenWindow } from "./window";
import { DEFAULT_LANGUAGE, GLOBAL_COOKIE_DOMAIN, LOCALSTORAGE_KEY, appVersion, isLocalDev } from "@config";
import { CDN_STATIC_URL } from "@config";
import { load as loadFingerprint } from "@fingerprintjs/fingerprintjs";
import report from "@report";
import { getPageDetailUrl } from "@routes";
import { USER_COUNTRY_MAP_BIZ } from "@src/config/country";
import { NavigatorLanguage } from "@src/config/language";
import { displayDateTime } from "@src/ui/DateTime";
import Cookies from "js-cookie";
import { v4 } from "uuid";

export const initPageLanguage = (): string => {
  const searchParams = new URLSearchParams(window.location.search);
  let language = searchParams.get("language");
  if (!language) {
    language = localStorage.getItem(LOCALSTORAGE_KEY.LANGUAGE);
  }
  if (!language) {
    language = NavigatorLanguage[navigator.language];
  }
  if (!language) {
    language = DEFAULT_LANGUAGE;
  }

  localStorage.setItem(LOCALSTORAGE_KEY.LANGUAGE, language);
  document.documentElement.lang = language;
  return language;
};

export const initTrackCode = (): string => {
  const url = new URL(window.location.href);
  const trackCode = url.searchParams.get("tc") || localStorage.getItem(LOCALSTORAGE_KEY.TRACK_CODE) || "";
  if (trackCode) {
    localStorage.setItem(LOCALSTORAGE_KEY.TRACK_CODE, trackCode);
  }
  return trackCode;
};

export async function getDeviceUUid() {
  let uuid = localStorage.getItem("device_uuid");
  if (!uuid) {
    const fpAgent = await loadFingerprint();
    const result = await fpAgent.get();
    uuid = result?.visitorId + String(new Date().getTime());
    localStorage.setItem("device_uuid", uuid);
  }
  return uuid;
}

const getInitDeviceId = () => {
  Cookies.set("appVersion", appVersion, {
    expires: 365,
    ...(GLOBAL_COOKIE_DOMAIN && { domain: GLOBAL_COOKIE_DOMAIN }),
    ...(!isLocalDev && { secure: true, sameSite: "None" as const }),
  });
  Cookies.set("deviceType", "pc", {
    expires: 365,
    ...(GLOBAL_COOKIE_DOMAIN && { domain: GLOBAL_COOKIE_DOMAIN }),
    ...(!isLocalDev && { secure: true, sameSite: "None" as const }),
  });
  let status = "init";

  return () => {
    return new Promise((resolve) => {
      const run = async () => {
        try {
          const cachedDeviceId = Cookies.get("deviceId");
          if (cachedDeviceId) {
            resolve(cachedDeviceId);
            return;
          }
          if (status === "init") {
            status = "loading";
            const fpAgent = await loadFingerprint();
            const result = await fpAgent.get();
            const deviceId = result?.visitorId;
            Cookies.set("deviceId", deviceId, {
              expires: 365,
              ...(GLOBAL_COOKIE_DOMAIN && { domain: GLOBAL_COOKIE_DOMAIN }),
              ...(!isLocalDev && { secure: true, sameSite: "None" as const }),
            });
            status = "finished";
            PubSub.publish("deviceId", deviceId);
            resolve(deviceId);
          } else {
            PubSub.subscribe("deviceId", (deviceId) => {
              resolve(deviceId);
            });
          }
        } catch (err) {
          report.error(err);
          resolve(v4());
        }
      };
      run();
    });
  };
};

export const initDeviceId = getInitDeviceId();

export const gotoTop = (domStr: string) => {
  const d = document.getElementById(domStr);
  d?.scrollIntoView({
    behavior: "smooth",
  });
};

export const getImgUrl = (url: string, address: string, suffix = ".png") => {
  return CDN_STATIC_URL + address + url + suffix;
};

export const hasSomeParentTheClass = (element: HTMLElement, classname: string, match?: HTMLElement): boolean => {
  if (!element) {
    return false;
  }
  if (element.parentNode === match) {
    return true;
  }
  if (element.id === "root") {
    return false;
  }
  if (element.className && element.className.split && element.className.split(" ").indexOf(classname) >= 0) return true;
  return !!(element.parentNode && hasSomeParentTheClass(element.parentNode as HTMLElement, classname, match));
};

export const clearStorage = () => {
  for (var key in localStorage) {
    if (key.indexOf("api.") === 0) {
      localStorage.removeItem(key);
    }
  }
};

export const arraySort = (items: any[], keys: string[], order: "descend" | "ascend") => {
  const newItems = [...items];
  newItems.sort((item1, item2) => {
    for (let key of keys) {
      if (item1[key] > item2[key]) {
        return order === "ascend" ? 1 : -1;
      } else if (item1[key] < item2[key]) {
        return order === "ascend" ? -1 : 1;
      } else {
        continue;
      }
    }
    return 0;
  });
  return newItems;
};

export const arrayRemove = (items: string[], key: string) => {
  return items.filter((item) => item !== key);
};

export const arrayFilterSearchWord = (items: any[], key: string, searchWord: string) => {
  return items.filter((item) => item[key]?.toLocaleLowerCase()?.indexOf(searchWord?.toLocaleLowerCase()) >= 0);
};

export const gotoTopY = (y: number = 0) => {
  window.scrollTo({
    top: y,
    left: 0,
    behavior: "smooth",
  });
};

export const toLowerCase = (str: string | null) => {
  if (!str) return;
  return str.substring(0, 1) + str.substring(1).toLowerCase();
};

export const toFirstUpCase = (str: string | null) => {
  if (!str) return;
  return str.substring(0, 1).toLocaleUpperCase() + str.substring(1).toLocaleLowerCase();
};

export const toAllFirstUpCase = (str: string | null) => {
  if (!str) return;
  return str
    .split(" ")
    .map((word) => toFirstUpCase(word))
    .join(" ");
};

export const isEmptyObject = (obj: {} = {}) => {
  return Object.keys(obj).length === 0;
};
/**
 * parent是否是child的向上父亲节点

 */
export const isDescendant = (parent: HTMLDivElement, child: HTMLDivElement) => {
  var node = child.parentNode;
  while (node !== null) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
};

export const digitalEncryption = (str: string | number) => {
  if (typeof str === "string" || typeof str === "number") {
    return String(str).replace(/-?\d+.*\d*/g, () => "*".repeat(4));
  }
};

export const sleep = (time: number) => {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      resolve(timer);
    }, time);
  });
};

export const checkObjIsEmpty = (object: object = {}) => {
  let flag = true;
  if (!Object.keys(object).length) return true;
  const objectValues = Object.values(object);
  for (let i = 0; i < objectValues.length; i++) {
    if (objectValues[i]) return false;
  }
  return flag;
};

export const fillterObjectEmpty = (object: object = {}) => {
  if (!Object.keys(object).length) return [];
  const objectValues = Object.values(object);
  return objectValues.filter((el) => !checkObjIsEmpty(el));
};

export const isUnitedStates = () => {
  if (window.KALO_DATA) {
    return window.KALO_DATA.reqCountry === "United States";
  }
};

export const isUnitedKingdom = () => {
  if (window.KALO_DATA) {
    return window.KALO_DATA.reqCountry === "United Kingdom";
  }
};

export const isChina = () => {
  if (window.KALO_DATA) {
    return window.KALO_DATA.reqCountry === "China";
  }
};

export const getTikTokImg = (url: string) => {
  return CDN_STATIC_URL + "/tiktok/" + url;
};

export const isMac = () => {
  return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
};

export const getCateListByChildID = (categoryMap: any, categoryID: string | null) => {
  if (categoryID) {
    let cateValue: { label: string; value: string }[] = [];
    // cateValue.push(categoryID);

    let tempCategoryID = categoryID;
    while (tempCategoryID) {
      if (categoryMap[tempCategoryID]) {
        cateValue.push({ label: categoryMap[tempCategoryID].label, value: tempCategoryID });
        tempCategoryID = categoryMap[tempCategoryID].parentID;
      } else {
        break;
      }
    }
    return [...cateValue].toReversed();
  }
  return null;
};

export const multiplyAndFormat = (input: string) => {
  if (!input) return "";
  const regex = /([><]?)(\d+(\.\d+)?)(?:(-)(\d+(\.\d+)?))?/g;
  const result = input.replace(regex, function (_match, prefix, num1, _, separator, num2) {
    const parsedNum1 = parseFloat(num1) * 100;
    let parsedNum2 = null;

    if (separator && num2 !== undefined) {
      parsedNum2 = parseFloat(num2) * 100;
    }
    if (separator && parsedNum2 !== null) {
      return `${prefix}${parsedNum1}-${parsedNum2}`;
    } else {
      return `${prefix}${parsedNum1}`;
    }
  });
  return result;
};

export const firstLetterCap = (str: string) => {
  return str.slice(0, 1).toLocaleUpperCase() + str.slice(1).toLocaleLowerCase();
};

export const useOpenPageDtail = () => {
  const { openWindow } = useOpenWindow();
  const openPageDetail = (type: string, params: any) => {
    openWindow(
      getPageDetailUrl({
        path: `/${type}/detail`,
        params,
      }),
    );
  };
  return { openPageDetail };
};

export function convertToUpperCase(pathname: string) {
  return pathname.toUpperCase().slice(1).replace(/[/-]/g, "_");
}

export function getElementTop(el: { offsetTop: any; offsetParent: any }) {
  let actualTop = el.offsetTop;
  let current = el.offsetParent;
  while (current !== null) {
    actualTop += current.offsetTop;
    current = current.offsetParent;
  }
  return actualTop;
}

export function formatCreatorDebutTime(debutTime?: string) {
  if (debutTime) {
    return displayDateTime({ dateTime: debutTime }) || "-";
  }
  return "-";
}

export function getSitePageType() {
  return location.pathname.slice(1);
}

export function getCurrentCountry() {
  return localStorage.getItem("region") || "US";
}

export function getCurrentLanguage() {
  return localStorage.getItem("language") || "en-US";
}

export function getCurrentCurrency() {
  return localStorage.getItem("currency") || "USD";
}

export function getReportSearchType() {
  return undefined;
}

export const handle360AdQhclickid = () => {
  const params360 = new URLSearchParams(location.search);
  const qhclickid = params360.get("qhclickid");
  if (qhclickid) {
    Cookies.set("qhclickid", qhclickid);
  }
};

export const uuid13 = () => {
  return v4().replace(/-/g, "").substring(0, 13);
};

export const formatToPercentage = (value: number | null, symbol: boolean = false): string => {
  if (value === null) return "";

  const percentage = value * 100;

  // 如果是整数，直接显示整数
  if (Number.isInteger(percentage)) {
    let res = `${percentage}%`;
    if (symbol && value > 0) {
      res = "+" + res;
    }
    return res;
  }

  // 如果是小数，保留必要的小数位数
  const formatted = percentage.toFixed(2);
  let res = formatted.endsWith(".00")
    ? `${Math.round(percentage)}%`
    : formatted.endsWith("0")
      ? percentage.toFixed(1) + "%"
      : `${formatted}%`;

  if (symbol && value > 0) {
    res = "+" + res;
  }
  return res;
};

export function findFilterByKey(key: string, filterConfig: any[] = []) {
  for (const group of filterConfig) {
    const found = group.children?.find((item: any) => item.key === key);
    if (found) {
      return found;
    }
  }
  return undefined;
}

export const formatNumberWithSymbol = (
  num: number,
  decimals: number = 0,
  symbol: boolean = false,
  isPercentage: boolean = false,
): string => {
  const formatNumber = (num: number, decimals: number = 0): string => {
    if (isNaN(num)) return "0";
    const fixed = Number(num).toFixed(decimals);
    const [intPart, decimalPart] = fixed.split(".");
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimalPart ? `${formattedInt}.${decimalPart}` : formattedInt;
  };

  const cb = isPercentage
    ? (num: number, _decimals: number = 0) => formatToPercentage(num)
    : (num: number, decimals: number = 0) => formatNumber(num, decimals);

  if (symbol && num > 0) {
    return "+" + cb(num, decimals);
  }
  return cb(num, decimals);
};

// extra s3 image name from url:
//   1. https://img.kalocdn.com/tiktok.product.images/tos-useast5-i-omjb5zjo8w-tx/4af40c4606794fae9ec3804389917bbc-large.png
//   2. https://resource.kaloclip.com/resources/2862520/upload/tiktok.product.images/tos-useast5-i-omjb5zjo8w-tx/4af40c4606794fae9ec3804389917bbc-large.png
export const extraImageNameFromUrl = (url: string) => {
  const urlObj = new URL(url);
  const pathname = urlObj.pathname;
  const segments = pathname.split("/");
  return segments[segments.length - 1] || "";
};

export function sliceProductCategory(productCategory: string | null | undefined): string[] {
  if (!productCategory || productCategory.trim() === "") return [];
  return productCategory.split(" > ").filter(Boolean);
}

export function transformAbbreviationCountry2FullCountry(country: string | undefined) {
  if (!country) return country;
  return USER_COUNTRY_MAP_BIZ[country.toUpperCase()] ?? country;
}

export const DEFAULT_VIDEO_LANGUAGE = "en";

export function formatKMB(count?: number, decimals = 0, suffix?: string): string | null {
  if (count === undefined || count === null) return null;

  const decimalPlaces = Number.isFinite(decimals) ? Math.max(0, Math.floor(decimals)) : 0;
  const units = [
    { threshold: 1e9, unit: "B" },
    { threshold: 1e6, unit: "M" },
    { threshold: 1e3, unit: "K" },
  ];
  const tail = suffix ? ` ${suffix}` : "";

  for (const { threshold, unit } of units) {
    if (count >= threshold) {
      const factor = 10 ** decimalPlaces;
      const value = Math.floor((count / threshold) * factor) / factor;
      return `${parseFloat(value.toFixed(decimalPlaces))}${unit}${tail}`;
    }
  }

  return `${count}${tail}`;
}

export const normalizeNameWithAt = (value?: string | null) => {
  if (!value) return "";
  return value.startsWith("@") ? value : `@${value}`;
};

export const isSeeDanceModel = (modelId: VideoCreationRequestInternalModelId | undefined) => {
  return modelId === VideoCreationRequestInternalModelId.sd2 || modelId === VideoCreationRequestInternalModelId.sd2f;
};

export { truncateUnicodeText, getUnicodeLength } from "./truncate";
